import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { useAuthState } from "@/auth/AuthProvider";
import { db } from "@/lib/firebase";
import { useRouteTitle } from "@/hooks/useRouteTitle";
import TimezoneSetting from "@/features/settings/TimezoneSetting";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";

export default function ProfilePage() {
  useRouteTitle("Profile");
  const { user } = useAuthState();
  const nav = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [courtJurisdiction, setCourtJurisdiction] = useState("");
  const [attorneyName, setAttorneyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // email change
  const [newEmail, setNewEmail] = useState("");
  const [emailCurrentPw, setEmailCurrentPw] = useState("");
  const [showEmailPw, setShowEmailPw] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  // password change
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurPw, setShowCurPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    async function loadProfile() {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        type ProfileRecord = {
          displayName?: unknown;
          courtJurisdiction?: unknown;
          attorneyName?: unknown;
        };
        const rawProfile = snap.get("profile") as ProfileRecord | undefined;
        if (rawProfile) {
          setDisplayName(typeof rawProfile.displayName === "string" ? rawProfile.displayName : "");
          setCourtJurisdiction(typeof rawProfile.courtJurisdiction === "string" ? rawProfile.courtJurisdiction : "");
          setAttorneyName(typeof rawProfile.attorneyName === "string" ? rawProfile.attorneyName : "");
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    }
    void loadProfile();
  }, [user]);

  async function handleChangeEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!user?.email) return;
    setEmailSaving(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, emailCurrentPw);
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, newEmail.trim());
      toast.success(`Verification email sent to ${newEmail.trim()}. Click the link to confirm the change.`, { duration: 7000 });
      setNewEmail("");
      setEmailCurrentPw("");
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        toast.error("Current password is incorrect");
      } else if (code === "auth/email-already-in-use") {
        toast.error("That email address is already in use");
      } else {
        toast.error((err as { message?: string }).message || "Failed to update email");
      }
    } finally {
      setEmailSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!user?.email) return;
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setPwSaving(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, curPw);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPw);
      toast.success("Password changed successfully");
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        toast.error("Current password is incorrect");
      } else {
        toast.error((err as { message?: string }).message || "Failed to change password");
      }
    } finally {
      setPwSaving(false);
    }
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      await updateProfile(user, { displayName: displayName.trim() });
      await setDoc(
        doc(db, "users", user.uid),
        {
          profile: {
            displayName: displayName.trim(),
            courtJurisdiction: courtJurisdiction.trim(),
            attorneyName: attorneyName.trim() || null,
          },
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setSaveSuccess(true);
      toast.success('Profile saved successfully');
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setError("Failed to save profile. Please try again.");
      toast.error("Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-800">
      {/* Blurred geometric background pattern */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="h-full w-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F58220' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px',
            filter: 'blur(2px)'
          }}
        />
      </div>

      <div className="relative space-y-6 p-6 max-w-4xl mx-auto">
        <header className="space-y-1">
          <h1 className="text-3xl font-semibold text-slate-100">Profile Settings</h1>
          <p className="text-sm text-slate-300">Update your profile details and preferences.</p>
        </header>

        <section className="space-y-4 rounded-2xl border border-brand-orange/50 bg-slate-900/90 backdrop-blur-sm p-6 shadow-2xl">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Profile</h2>
          <p className="text-sm text-slate-300">These details appear on case filings and notifications.</p>
        </div>
        {error ? (
          <div className="rounded border border-red-500/50 bg-red-900/50 px-3 py-2 text-sm text-red-200" role="alert">
            {error}
          </div>
        ) : null}
        {saveSuccess ? (
          <div className="rounded border border-emerald-500/50 bg-emerald-900/50 px-3 py-2 text-sm text-emerald-200" role="status">
            ✓ Profile saved successfully
          </div>
        ) : null}
        <form onSubmit={onSave} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="displayName" className="text-sm font-medium text-slate-200">
              Display Name <span className="text-red-400">*</span>
            </label>
            <input
              id="displayName"
              name="displayName"
              className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:bg-slate-800 disabled:text-slate-400"
              placeholder="Your full name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={loading || saving}
              minLength={2}
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="courtJurisdiction" className="text-sm font-medium text-slate-200">
              Court Jurisdiction <span className="text-red-400">*</span>
            </label>
            <input
              id="courtJurisdiction"
              name="courtJurisdiction"
              className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:bg-slate-800 disabled:text-slate-400"
              placeholder="e.g., Multnomah County, OR"
              type="text"
              value={courtJurisdiction}
              onChange={(e) => setCourtJurisdiction(e.target.value)}
              required
              disabled={loading || saving}
              minLength={2}
              maxLength={200}
            />
            <p className="text-xs text-slate-400">Include county and state where your case is filed</p>
          </div>
          <div className="space-y-2">
            <label htmlFor="attorneyName" className="text-sm font-medium text-slate-200">
              Attorney Name (optional)
            </label>
            <input
              id="attorneyName"
              name="attorneyName"
              className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:bg-slate-800 disabled:text-slate-400"
              placeholder="Attorney's full name"
              type="text"
              value={attorneyName}
              onChange={(e) => setAttorneyName(e.target.value)}
              disabled={loading || saving}
              maxLength={100}
            />
          </div>

          <div className="border-t border-slate-600 pt-4">
            <TimezoneSetting />
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              className="rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-orange/90 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={saving || loading || !displayName.trim() || !courtJurisdiction.trim()}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={() => nav("/cases")}
              className="rounded-md border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 shadow-sm transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
              disabled={saving}
            >
              Back to Dashboard
            </button>
            <button
              type="button"
              onClick={() => nav("/billing")}
              className="rounded-md border border-brand-orange/50 bg-brand-orange/10 px-4 py-2 text-sm font-medium text-brand-orange shadow-sm transition hover:bg-brand-orange/20 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:ring-offset-2"
            >
              Manage Billing →
            </button>
          </div>
        </form>
        {loading ? <p className="text-xs text-slate-300">Loading profile…</p> : null}
      </section>

      {/* ── Email Address ─────────────────────────────────────── */}
      <section className="space-y-4 rounded-2xl border border-slate-700 bg-slate-900/90 backdrop-blur-sm p-6 shadow-2xl">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Email Address</h2>
          <p className="text-sm text-slate-300">
            Current: <span className="text-slate-100 font-medium">{user?.email}</span>
          </p>
        </div>
        <form onSubmit={handleChangeEmail} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="newEmail" className="text-sm font-medium text-slate-200">
              New Email Address <span className="text-red-400">*</span>
            </label>
            <input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={emailSaving}
              placeholder="new@example.com"
              autoComplete="email"
              required
              className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:opacity-50"
            />
            <p className="text-xs text-slate-400">A verification link will be sent to the new address before the change takes effect.</p>
          </div>
          <div className="space-y-2">
            <label htmlFor="emailCurrentPw" className="text-sm font-medium text-slate-200">
              Current Password <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                id="emailCurrentPw"
                type={showEmailPw ? "text" : "password"}
                value={emailCurrentPw}
                onChange={(e) => setEmailCurrentPw(e.target.value)}
                disabled={emailSaving}
                placeholder="Your current password"
                autoComplete="current-password"
                required
                className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 pr-10 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:opacity-50"
              />
              <button type="button" onClick={() => setShowEmailPw((s) => !s)} tabIndex={-1} aria-label={showEmailPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200">
                {showEmailPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-slate-400">Required to confirm your identity before changing your email.</p>
          </div>
          <button
            type="submit"
            disabled={emailSaving || !newEmail.trim() || !emailCurrentPw}
            className="rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {emailSaving ? "Sending…" : "Send Verification Email"}
          </button>
        </form>
      </section>

      {/* ── Change Password ───────────────────────────────────── */}
      <section className="space-y-4 rounded-2xl border border-slate-700 bg-slate-900/90 backdrop-blur-sm p-6 shadow-2xl">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Change Password</h2>
          <p className="text-sm text-slate-300">Update your login password. You'll need to enter your current password to confirm.</p>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {([
            { id: "curPw", label: "Current Password", val: curPw, set: setCurPw, show: showCurPw, setShow: setShowCurPw, hint: "", auto: "current-password" },
            { id: "newPw", label: "New Password", val: newPw, set: setNewPw, show: showNewPw, setShow: setShowNewPw, hint: "Minimum 8 characters.", auto: "new-password" },
            { id: "confirmPw", label: "Confirm New Password", val: confirmPw, set: setConfirmPw, show: showConfirmPw, setShow: setShowConfirmPw, hint: "", auto: "new-password" },
          ] as const).map(({ id, label, val, set, show, setShow, hint, auto }) => (
            <div key={id} className="space-y-2">
              <label htmlFor={id} className="text-sm font-medium text-slate-200">
                {label} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  id={id}
                  type={show ? "text" : "password"}
                  value={val}
                  onChange={(e) => set(e.target.value)}
                  disabled={pwSaving}
                  autoComplete={auto}
                  required
                  minLength={id !== "curPw" ? 8 : undefined}
                  className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 pr-10 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:opacity-50"
                />
                <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1} aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {hint && <p className="text-xs text-slate-400">{hint}</p>}
            </div>
          ))}
          <button
            type="submit"
            disabled={pwSaving || !curPw || !newPw || !confirmPw}
            className="rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pwSaving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </section>
      </div>
    </div>
  );
}
